import {
  customAlertApiRef,
  WbDrawer,
  WbDrawerContent,
  WbTable,
} from '@agilelab/plugin-wb-platform';
import { useApolloClient } from '@apollo/client';
import { useApi } from '@mainstage/core-plugin-api';
import { TableBody, Typography } from '@material-ui/core';
import { FC, useEffect, useMemo, useState } from 'react';
import useAsyncFn from 'react-use/lib/useAsyncFn';
import {
  GOVERNANCE_ENTITIES,
  GovernanceEntitiesQueryType,
} from '../../../graphql/governanceEntities';
import { DagStatus } from '@agilelab/plugin-wb-builder-common';
import { Test } from '../../EditorPage/types';
import { getErrorMessage } from '../utils';
import {
  ValidationTestDrawerContextProvider,
  useValidationTestDrawerContext,
} from './ValidationTestDrawerContext';
import { ValidationTestErrorHeader } from './ValidationTestErrorHeader';
import { ValidationTestRow } from './ValidationTestRow';

interface Props {
  open: boolean;
  onClose: () => void;
  tests: { loading: boolean; value?: { test: Test | undefined } };
  version: string | undefined;
  projectName: string;
}

const STATUS_GRAVITY = new Map([
  [DagStatus.FAILED, 10],
  [DagStatus.ERROR, 10],
  [DagStatus.WARNING, 7],
  [DagStatus.INFO, 6],
  [DagStatus.NOT_BLOCKING_ERROR, 5],
]);

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const DEFAULT_PAGE_SIZE = 25;

const ValidationTestDrawerContent: FC<Props> = ({
  open,
  onClose,
  tests,
  version,
  projectName,
}) => {
  const client = useApolloClient();
  const alertApi = useApi(customAlertApiRef);
  const {
    validationTestResultDetailContent,
    validationTestResultOpen,
    setValidationTestResultOpen,
  } = useValidationTestDrawerContext();

  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const [asyncGovernanceEntities, fetchAsyncGovernanceEntities] =
    useAsyncFn(async () => {
      try {
        if (tests.loading || !tests.value) return undefined;

        const ids = (tests.value.test?.tasks || [])
          .filter(t => !!t.governanceEntityId)
          .map(p => p.governanceEntityId);

        if (ids.length === 0) return undefined;

        const queryResponse = await client.query<GovernanceEntitiesQueryType>({
          query: GOVERNANCE_ENTITIES,
          variables: { ids },
        });

        return { governanceEntities: queryResponse.data };
      } catch (error) {
        alertApi.post({ error, severity: 'error' });
        return undefined;
      }
    }, [tests]);

  useEffect(() => {
    if (open) {
      fetchAsyncGovernanceEntities();
      setPage(0);
      setPageSize(DEFAULT_PAGE_SIZE);
    }
  }, [fetchAsyncGovernanceEntities, open]);

  const errorMessage = useMemo(() => {
    const test = tests?.value?.test;
    return test && getErrorMessage(test);
  }, [tests?.value?.test]);

  const allTasks = useMemo(
    () =>
      [...(tests.value?.test?.tasks || [])].sort(
        (a, b) =>
          (STATUS_GRAVITY.get(b.status) ?? 0) -
          (STATUS_GRAVITY.get(a.status) ?? 0),
      ),
    [tests.value?.test?.tasks],
  );
  const paginatedTasks = allTasks.slice(pageSize * page, pageSize * (page + 1));

  return (
    <>
      <WbDrawer open={open} setOpen={onClose} title="Validation Test">
        <WbDrawerContent>
          <Typography
            style={{
              fontWeight: 'normal',
              marginBottom: errorMessage ? 0 : 16,
            }}
            color="primary"
            variant="h6"
          >
            <b>{version}</b> of {projectName}
          </Typography>

          {errorMessage && (
            <ValidationTestErrorHeader errorMessage={errorMessage} />
          )}

          <WbTable<Test>
            stickyHeader
            components={{
              tableLoader: {
                loading: tests.loading || asyncGovernanceEntities.loading,
              },
              tableContent: {
                body: (
                  <TableBody>
                    {paginatedTasks.map((task, i) => {
                      return (
                        <ValidationTestRow
                          key={i}
                          task={task}
                          governanceEntities={
                            asyncGovernanceEntities.value?.governanceEntities
                              ?.cgp_governance_entity || []
                          }
                        />
                      );
                    })}
                  </TableBody>
                ),
              },
            }}
            pagination={{
              count: allTasks.length,
              limit: pageSize,
              offset: pageSize * page,
              onPageChange: (newPage: number) => setPage(newPage),
              onRowsPerPageChange: (newPageSize: number) => {
                setPageSize(newPageSize);
                setPage(0);
              },
              rowsPerPageOptions: PAGE_SIZE_OPTIONS,
            }}
          />
        </WbDrawerContent>
      </WbDrawer>

      <WbDrawer
        open={validationTestResultOpen}
        setOpen={setValidationTestResultOpen}
        title="Result Details"
      >
        <WbDrawerContent>{validationTestResultDetailContent}</WbDrawerContent>
      </WbDrawer>
    </>
  );
};

export const ValidationTestDrawer: FC<Props> = props => (
  <ValidationTestDrawerContextProvider>
    <ValidationTestDrawerContent {...props} />
  </ValidationTestDrawerContextProvider>
);
